import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';


@Injectable()
export class SiteAssetsService extends BaseHttpClientService {

  constructor(
    private http: HttpClient,
  ) {
    super()
  }

  public getAllSiteAssets(siteid, pagination, sort, filterText) {
    let queryString = '?size=' + pagination.size + '&page=' + pagination.page +  '&sort=' + sort + '&name=' + filterText;
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-asset/by-site/' + siteid + queryString, { observe: 'response', headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public getAssetById(assetId) {
    return this.http.get(environment.apiUrl + 'api/ext-asset/' + assetId)
      .pipe(catchError(this.handleError));
  }

  public addAssetRestriction(restriction) {
    return this.http.post(environment.apiUrl + 'api/ext-restriction/' , restriction)
      .pipe(catchError(this.handleError));
  }

  public removeAssetRestriction(restrictionId: number) {
    return this.http.put(environment.apiUrl + 'api/ext-restriction/' + restrictionId + '/in-activate', {})
      .pipe(catchError(this.handleError));
  }

  public getAllSiteAssetsNoPagination(id) {
    // Summary endpoint (id/name/locked/exemptFromAssetScheduling/assetCategories/manufacturer/model/rating)
    // used by the work-order create form, choose-work-order modal and create-from-procedure page.
    // Revert to 'api/ext-asset/by-site/' if the full payload is ever needed here.
    return this.http.get(environment.apiUrl + 'api/ext-asset/summary/by-site/' + id + '?size=10000&page=0&sort=name,asc')
      .pipe(catchError(this.handleError));
  }

  public deleteAsset(id) {
    return this.http.delete(environment.apiUrl + 'api/ext-asset/' + id)
      .pipe(catchError(this.handleError));
  }

  // Create Work Order
  public addAssetToSite(obj): Observable<any> {
    const jsonDataMultipartName = 'asset';
    const fileMultipartName = 'image';

    const body = {
      name: obj.name,
      description: obj.description,
      manufacturer: obj.manufacturer,
      model: obj.model,
      rating: obj.rating,
      latitude: obj.latitude,
      longitude: obj.longitude,
      siteId: obj.siteId,
      assetCategoryIds: obj.assetCategoryIds,
      competencyTypeIds: obj.competencyTypeIds
    };

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    obj.imageArray.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http.post(environment.apiUrl + 'api/ext-asset/add-asset-to-site', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Asset image file is infected and cannot be uploaded');
        }
        return this.handleError(error);
      }));
  }

  public editAsset(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-asset', obj)
      .pipe(catchError(this.handleError));
  }

  public setAssetLockStatus(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-asset/set-locked', obj)
      .pipe(catchError(this.handleError));
  }

  public createAssetCertificate(assetCert: any): Observable<any> {
    const requestBody = {
      title: assetCert.title,
      assetId: assetCert.assetId,
      expiryDate: assetCert.expiryDate,
      lockAssetOnExpiry: assetCert.lockAssetOnExpiry
    };
    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append('certificate', new Blob([JSON.stringify(requestBody)], { type: 'application/json' }));
    formData.append('certDoc', assetCert.certFile, assetCert.certFile.name);
    return this.http.post(environment.apiUrl + 'api/ext-asset-certificate', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Certificate file is infected and cannot be uploaded');
        }
        return this.handleError(error);
      }));
  }

  public replaceAssetCertificateDocument(assetCertId: any, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('certDoc', file, file.name);
    return this.http.post(environment.apiUrl + 'api/ext-asset-certificate/' + assetCertId + '/replace-document', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Certificate file is infected and cannot be uploaded');
        }
        return this.handleError(error);
      }));
  }

  public updateAssetCertificate(assetCert, assetCertId): Observable<any> {
    return this.http.put(environment.apiUrl + 'api/ext-asset-certificate/' + assetCertId, assetCert)
      .pipe(catchError(this.handleError));
  }

  public deleteAssetCertificate(assetCertificateId: any) {
    return this.http.delete(environment.apiUrl + 'api/ext-asset-certificate/' + assetCertificateId)
      .pipe(catchError(this.handleError));
  }

  public handleDataWithHeaders(res: HttpResponse<any>) {
    const headers = res.headers;
    const body = res.body;
    const data = {
      'headers': headers,
      'body': body
    };
    return data;
  }

  public showAssetCertificate(fileName: String): void {
    const uri = environment.apiUrl + 'api/ext-file/assetCertificates/' + fileName;
    this.getFile(uri);
  }

  private getFile(uri: string) {
    this.http.get(uri, { responseType: 'blob' })
      .subscribe(response => {
        const fileURL = URL.createObjectURL(response);
        window.open(fileURL, '_blank');
      });
  }
}
